import type { TreeNode } from '../rag/Tree/Tree';

/**
 * Pure helpers for FileTreePicker's lazy folder loading.
 *
 * Background: the storage backend lists folders differently per provider. The S3
 * strategy returns the whole subtree in one root call, so folder nodes already
 * arrive with their children. The SharePoint strategy returns only a folder's
 * immediate children, so subfolders arrive empty and their contents must be
 * fetched on demand when the user expands them. These helpers keep that logic
 * unit-testable without rendering the (react-dnd–heavy) Tree.
 */

/**
 * Convert a folder node's stored `path` into the path segment expected by
 * `storageAPI.getFileOrListFolder`, which builds `.../storage/{volume}/{path}`
 * and the server re-`decodeURIComponent`s it, treating a trailing slash as a
 * folder listing.
 *
 * - strips leading/trailing slashes — S3 folder paths look like `/General/`
 *   while SharePoint folder paths look like `General`, and both must produce the
 *   same request path;
 * - percent-encodes each segment so folder names with spaces or reserved
 *   characters (e.g. `Shared Documents`) survive the round-trip;
 * - re-appends exactly one trailing slash so the server routes to `listFolder`.
 *
 * Returns `''` for the root (which is never lazily loaded).
 */
export const folderPathToRequestPath = (folderPath: string): string => {
  const segments = folderPath
    .split('/')
    .filter(segment => segment !== '')
    .map(segment => encodeURIComponent(segment));
  return segments.length ? `${segments.join('/')}/` : '';
};

/**
 * Immutably replace the `files` of the folder node whose `path` equals
 * `targetPath`, returning a new tree. Only the branch that actually changes gets
 * new node objects; every node keeps its `id`, so the Tree renderer (which keys
 * by `id`) preserves component instances — the expanded folder stays open and
 * sibling folders keep their own open/closed state. Returns the original array
 * reference when `targetPath` is not found, so untouched branches don't re-render.
 */
export const replaceFolderChildren = (
  nodes: TreeNode[],
  targetPath: string,
  children: TreeNode[]
): TreeNode[] => {
  let changed = false;
  const next = nodes.map(node => {
    if (node.type === 'folder' && node.path === targetPath) {
      changed = true;
      return { ...node, files: children };
    }
    if (node.type === 'folder' && node.files && node.files.length > 0) {
      const updatedFiles = replaceFolderChildren(node.files, targetPath, children);
      if (updatedFiles !== node.files) {
        changed = true;
        return { ...node, files: updatedFiles };
      }
    }
    return node;
  });
  return changed ? next : nodes;
};

/**
 * Split a path into its non-empty segments, so the two path shapes compare alike.
 *
 * S3 folder paths look like `/General/`, SharePoint ones like `General` — and their files
 * correspondingly. Comparing the strings would make `/General/` and `General` two different
 * folders; comparing segments makes them one.
 */
const pathSegments = (path: string): string[] => path.split('/').filter(segment => segment !== '');

/**
 * Is `path` strictly below `folderPath`?
 *
 * The server-side folder walk answers the same question over the storage listing (`isBelowFolder`
 * in tri-server's `contextAttachFromStorage.ts`). This is its client-side twin, used to keep the
 * selection free of picks that a picked folder already covers.
 */
export const isUnderFolder = (folderPath: string, path: string): boolean => {
  const folder = pathSegments(folderPath);
  const candidate = pathSegments(path);
  if (candidate.length <= folder.length) {
    return false;
  }
  return folder.every((segment, index) => candidate[index] === segment);
};

/** Is `path` covered by any of the picked folders? */
export const isCoveredByPickedFolder = (path: string, pickedFolderPaths: string[]): boolean =>
  pickedFolderPaths.some(folderPath => isUnderFolder(folderPath, path));

/**
 * Stamp the current multi-select state onto a tree.
 *
 * Selection is tracked by `path` outside the tree, because the tree is rebuilt as folders lazily
 * load and a node object is not a stable identity. Nodes that do not change keep their object (and
 * therefore their `id`, which the Tree renderer keys by), so re-stamping a selection does not
 * remount folders and collapse them.
 *
 * **A picked folder means its whole subtree**, so everything below it is stamped `selected` too —
 * and `selectionLocked`, because that check follows from the folder and cannot be taken back on its
 * own (the request carries the folder as a path; the server resolves what is inside it). Without
 * this, checking a folder left every file under it visibly unchecked, which read as "the folder
 * came along but its contents did not" (Dr. Sasse, 2026-09-04) — the files *were* attached.
 *
 * Inheritance is carried down the walk rather than derived per node from the picked paths: the tree
 * is already nested parent-to-child, so an ancestor's pick is known by the time its children are
 * stamped, and lazily loaded children inherit it the moment they are spliced in.
 */
export const applySelectedPaths = (
  nodes: TreeNode[],
  selectedPaths: Set<string>,
  inheritedFromFolder = false
): TreeNode[] => {
  let changed = false;
  const next = nodes.map(node => {
    const pickedItself = !!node.path && selectedPaths.has(node.path);
    const selected = pickedItself || inheritedFromFolder;
    // A folder the user picked passes the pick on to its children; so does a folder that only
    // inherited it, so the whole subtree is covered however deep it goes.
    const childrenInherit = inheritedFromFolder || (node.type === 'folder' && pickedItself);
    const files = node.files
      ? applySelectedPaths(node.files, selectedPaths, childrenInherit)
      : node.files;
    if (
      selected === !!node.selected &&
      inheritedFromFolder === !!node.selectionLocked &&
      files === node.files
    ) {
      return node;
    }
    changed = true;
    return {
      ...node,
      selected,
      selectionLocked: inheritedFromFolder,
      ...(files ? { files } : {}),
    };
  });
  return changed ? next : nodes;
};

/**
 * Collect the paths of all folders that already arrived with children — i.e.
 * the folders the backend fully populated in the initial listing (the S3 case).
 * These are seeded as "already loaded" so expanding them never triggers a
 * redundant (and, for S3's recursive listing, mis-nesting) lazy fetch. Folders
 * that arrive empty (the SharePoint case) are deliberately absent, so they fetch
 * their contents on first expand.
 */
export const collectLoadedFolderPaths = (nodes: TreeNode[]): Set<string> => {
  const loaded = new Set<string>();
  const walk = (current: TreeNode[]): void => {
    current.forEach(node => {
      if (node.type === 'folder' && node.path && node.files && node.files.length > 0) {
        loaded.add(node.path);
        walk(node.files);
      }
    });
  };
  walk(nodes);
  return loaded;
};
